// Lookahead scheduler (Chris Wilson "A Tale of Two Clocks"). A setInterval polls every
// ~25ms and pulls generative intents due within a ~120ms lookahead window, dispatching
// them to the engine at sample-accurate ctx times. All times are ctx.currentTime-based.
import type { StrikeIntent } from '@shared/intents';

const INTERVAL_MS = 25;
const LOOKAHEAD_SEC = 0.12;

export interface SchedulerHooks {
  /** advance the generative engine; dt is real seconds. */
  update: (nowSec: number, dtSec: number) => void;
  /** pull due intents up to nowSec + horizon. */
  collect: (nowSec: number, horizonSec: number) => StrikeIntent[];
  /** fire one intent (resolve voice, post to engine, trigger visuals). */
  fire: (intent: StrikeIntent) => void;
}

export class Scheduler {
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastTick = 0;

  constructor(
    private readonly now: () => number, // ctx.currentTime
    private readonly hooks: SchedulerHooks,
  ) {}

  start(): void {
    if (this.timer !== null) return;
    this.lastTick = this.now();
    this.timer = setInterval(() => this.tick(), INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  get running(): boolean {
    return this.timer !== null;
  }

  private tick(): void {
    const now = this.now();
    const dt = Math.max(0, now - this.lastTick);
    this.lastTick = now;
    this.hooks.update(now, dt);
    const due = this.hooks.collect(now, LOOKAHEAD_SEC);
    for (const intent of due) {
      // never schedule in the past
      if (intent.whenSec < now) intent.whenSec = now + 0.002;
      this.hooks.fire(intent);
    }
  }
}
